import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';
import { Paper, PaperLink } from 'js/social/shared/types';
import { getWork } from 'js/social/api/work_api';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import PaperCard from 'js/social/PaperContainer';
import { AttachmentOverlay } from 'js/social/shared/attachments/AttachmentOverlay';
import { serializePaperLink } from './utils';
import { AttachmentStatus, Banner, LoadingBanner } from './useAttachment';
import { PaperSelector } from 'js/social/shared/attachments/PaperSelector/PaperSelector';
import { InfoIcon } from 'js/social/shared/Icons';

const PaperContainer = styled(AttachmentOverlay)`
  margin: 8px 0;
`;

export function usePaperAttachment(initialPaper?: Paper, removable?: boolean) {
  const [status, setStatus] = useState(
    initialPaper === undefined
      ? AttachmentStatus.Empty
      : AttachmentStatus.Ready,
  );
  const [error, setError] = useState<string>();
  const [paper, setPaper] = useState<Paper | undefined>(initialPaper);
  const [searching, setSearching] = useState(false);
  const currentLink = useRef<PaperLink>();
  const linkTimeout = useRef<number>();

  const remove = useCallback(() => {
    setStatus(AttachmentStatus.Empty);
    setPaper(undefined);
    setError(undefined);
    setSearching(false);
  }, []);

  const attach = useCallback(
    async (payload?: { paper?: Paper; link?: PaperLink }) => {
      remove();

      if (!payload) {
        currentLink.current = undefined;
        setSearching(true);
      } else if (payload.link) {
        setStatus(AttachmentStatus.Loading);
        try {
          const paper = await getWork(payload.link);
          // Make sure we're still on the paper
          if (
            serializePaperLink(payload.link) ===
            serializePaperLink(currentLink.current)
          ) {
            setPaper(paper);
            setStatus(AttachmentStatus.Ready);
          }
        } catch (e) {
          // TS won't let us annotate the catch clause
          setError((e as Error).message);
          setStatus(AttachmentStatus.Empty);
        }
      } else if (payload.paper) {
        currentLink.current = undefined;
        setPaper(payload.paper);
        setStatus(AttachmentStatus.Ready);
      }
    },
    [remove],
  );

  const content: ReactNode = useMemo(() => {
    if (searching) {
      return <PaperSelector onChange={(paper) => attach({ paper })} />;
    } else if (status === AttachmentStatus.Loading) {
      return (
        <LoadingBanner icon={<Spinner />}>
          Your link is loading, it will be viewable shortly.
        </LoadingBanner>
      );
    } else if (status === AttachmentStatus.Ready && paper) {
      return (
        <PaperContainer removable={removable} onRemove={remove}>
          <PaperCard work={paper} style={{ padding: 0 }} />
        </PaperContainer>
      );
    } else if (error) {
      return (
        <Banner type="warning" icon={<InfoIcon />}>
          {error}
        </Banner>
      );
    }
    return null;
  }, [searching, status, paper, error, attach, remove, removable]);

  const handlePaperLinksChange = useCallback(
    (newLinks: PaperLink[], removedLinks: PaperLink[]) => {
      if (
        removedLinks.some(
          (link) =>
            serializePaperLink(currentLink.current) ===
            serializePaperLink(link),
        )
      ) {
        currentLink.current = undefined;
        remove();
      }

      if (!paper && currentLink.current === undefined && newLinks.length > 0) {
        currentLink.current = newLinks[0];
        setStatus(AttachmentStatus.Loading);
        window.clearTimeout(linkTimeout.current);
        linkTimeout.current = window.setTimeout(() => {
          attach({ link: newLinks[0] });
        }, 250);
      }
    },
    [paper, attach, remove],
  );

  return {
    paper,
    status,
    content,
    onAttach: attach,
    onPaperLinksChange: handlePaperLinksChange,
  };
}
